import getServiceVersionless from './getServiceVersionless';

export const SELF_SERVICE_DNR_FLOW_TYPES = {
  TRIAL: 'trial',
  SUBSCRIPTION: 'subscription'
};

export const TRIAL_DNR_ELIGIBLE_SERVICES = [
  'website_tools_bundle',
  'zb_money_pro'
];

/**
 * service must match the value returned by getServiceVersionless(subscription.service).
 * Versioned services are matched by normalizing the subscription service before lookup.
 */
export const SELF_SERVICE_DNR_ELIGIBLE_SUBSCRIPTIONS = [
  {
    service: 'domain_name_reg_sqsp_tier1',
    billingInterval: 'yearly',
    contractInterval: 'annual',
    requireFormationComplete: false,
    requirePostPurchaseComplete: false
  },
  {
    service: 'domain_name_reg_sqsp_tier2',
    billingInterval: 'yearly',
    contractInterval: 'annual',
    requireFormationComplete: false,
    requirePostPurchaseComplete: false
  },
  {
    service: 'domain_name_reg_sqsp_tier3',
    billingInterval: 'yearly',
    contractInterval: 'annual',
    requireFormationComplete: false,
    requirePostPurchaseComplete: false
  },
  {
    service: 'domain_name_reg_sqsp_tier4',
    billingInterval: 'yearly',
    contractInterval: 'annual',
    requireFormationComplete: false,
    requirePostPurchaseComplete: false
  },
  {
    service: 'website_tools_bundle',
    billingInterval: 'monthly',
    contractInterval: 'monthly',
    requireFormationComplete: false,
    requirePostPurchaseComplete: false
  },
  {
    service: 'zb_website_tools_bundle',
    billingInterval: 'monthly',
    contractInterval: 'monthly',
    requireFormationComplete: false,
    requirePostPurchaseComplete: false
  },
  {
    service: 'zb_money_pro',
    billingInterval: 'monthly',
    contractInterval: 'monthly',
    requireFormationComplete: false,
    requirePostPurchaseComplete: false
  }
];

const BLOCKED_SELF_SERVICE_DNR_STATUSES = [
  'deferred',
  'created',
  'unpaid',
  'cancel_renewal',
  'cancel_renewal_pending'
];

const BLOCKED_TRIAL_CANCEL_STATUSES = [
  'cancel_renewal',
  'cancel_renewal_pending'
];

const SELF_SERVICE_DNR_ACTIONABLE_STATUSES = ['active', 'paid'];
const GOOD_SELF_SERVICE_DNR_PAYMENT_STATUSES = ['paid', 'succeeded'];

export function getPaymentStatus(subscription) {
  return subscription?.payment?.[0]?.paymentStatus || '';
}

// Preserve legacy trial cancel behavior: any known non-failed payment status can
// cancel a trial, while paid subscription DNR uses a stricter allowlist.
function hasTrialCancelPaymentStatus(paymentStatus) {
  return Boolean(paymentStatus) && paymentStatus !== 'failed';
}

function hasGoodSelfServiceDnrPaymentStatus(paymentStatus) {
  return GOOD_SELF_SERVICE_DNR_PAYMENT_STATUSES.includes(paymentStatus);
}

function hasSelfServiceDnrActionableStatus(subscriptionStatus) {
  return SELF_SERVICE_DNR_ACTIONABLE_STATUSES.includes(subscriptionStatus);
}

function isSubscriptionDnrBlockedStatus(subscriptionStatus) {
  return BLOCKED_SELF_SERVICE_DNR_STATUSES.includes(subscriptionStatus);
}

function isTrialCancelBlockedStatus(subscriptionStatus) {
  return BLOCKED_TRIAL_CANCEL_STATUSES.includes(subscriptionStatus);
}

function getSelfServiceDnrAllowlistEntry(
  subscription,
  eligibleSubscriptions = SELF_SERVICE_DNR_ELIGIBLE_SUBSCRIPTIONS
) {
  const service = getServiceVersionless(subscription?.service);

  return eligibleSubscriptions.find((entry) => (
    entry.service === service
    && entry.billingInterval === subscription?.period
    && entry.contractInterval === subscription?.contractInterval
  ));
}

function meetsSelfServiceDnrCompletionRequirements(entry, subscription) {
  return (!entry?.requireFormationComplete || Boolean(subscription?.isFormationComplete))
    && (!entry?.requirePostPurchaseComplete || Boolean(subscription?.isPostPurchaseComplete));
}

/**
 * Central DNR decision point for subscription menu and route consumers.
 * Consumers should rely on canCancelTrial, canCancelSubscription, and cancelFlowType
 * for UI decisions; the remaining returned booleans are diagnostics for tests and
 * future route guards. Passing paymentStatus overrides subscription.payment[0].paymentStatus,
 * so callers should only pass the same payment record being displayed to the customer.
 * isSubscriptionDnrEnabled gates the paid subscription menu entry until the paid
 * cancel route/copy/analytics work ships in GROWTH-3961.
 *
 * @param {object} root0 Decision inputs.
 * @param {object} root0.subscription Billing subscription record.
 * @param {string} [root0.paymentStatus] Payment status override.
 * @param {boolean} [root0.isIncluded] Whether the row is included in another subscription.
 * @param {boolean} [root0.isSubscriptionDnrEnabled] Paid DNR feature gate.
 * @param {object[]} [root0.eligibleSubscriptions] Paid DNR allowlist override.
 * @returns {object} Self-service DNR state.
 */
export function getSelfServiceDnrState({
  subscription,
  paymentStatus = getPaymentStatus(subscription),
  isIncluded = false,
  isSubscriptionDnrEnabled = false,
  eligibleSubscriptions = SELF_SERVICE_DNR_ELIGIBLE_SUBSCRIPTIONS
}) {
  const service = getServiceVersionless(subscription?.service);
  const isTrial = Boolean(subscription?.inTrial);
  const allowlistEntry = getSelfServiceDnrAllowlistEntry(subscription, eligibleSubscriptions);
  const isAllowlisted = Boolean(allowlistEntry);
  const isTrialEligible = TRIAL_DNR_ELIGIBLE_SERVICES.includes(service);
  const hasSubscriptionDnrBlockedStatus = isSubscriptionDnrBlockedStatus(
    subscription?.subscriptionStatus
  );
  const hasTrialCancelBlockedStatus = isTrialCancelBlockedStatus(subscription?.subscriptionStatus);
  const hasTrialPayment = hasTrialCancelPaymentStatus(paymentStatus);
  const hasGoodPayment = hasGoodSelfServiceDnrPaymentStatus(paymentStatus);
  const hasActionableStatus = hasSelfServiceDnrActionableStatus(
    subscription?.subscriptionStatus
  );

  const canCancelTrial = isTrial
    && isTrialEligible
    && !isIncluded
    && hasTrialPayment
    && !hasTrialCancelBlockedStatus;

  // Paid subscription DNR here is the new allowlisted self-service path.
  // Legacy UJM/support-routing policy remains in the menu until that flow is retired.
  const canCancelSubscription = isSubscriptionDnrEnabled
    && !isTrial
    && !isIncluded
    && isAllowlisted
    && meetsSelfServiceDnrCompletionRequirements(allowlistEntry, subscription)
    && hasGoodPayment
    && hasActionableStatus
    && !hasSubscriptionDnrBlockedStatus;

  let cancelFlowType = null;
  if (canCancelSubscription) {
    cancelFlowType = SELF_SERVICE_DNR_FLOW_TYPES.SUBSCRIPTION;
  } else if (canCancelTrial) {
    cancelFlowType = SELF_SERVICE_DNR_FLOW_TYPES.TRIAL;
  }

  return {
    service,
    isTrial,
    isAllowlisted,
    hasSubscriptionDnrBlockedStatus,
    hasTrialCancelBlockedStatus,
    hasGoodPayment,
    hasActionableStatus,
    canCancelTrial,
    canCancelSubscription,
    cancelFlowType
  };
}
